import React, { useState } from "react";
import SummaryApi from "./common/index";

// --- Shared validation helpers (same as slider file) ---
const isValidMobile = (value) => /^[0-9]{10}$/.test(value);
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const Popup = ({ onClose }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    mobile: "",
    email: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const trimmedName = formData.fullName.trim();
  const trimmedMobile = formData.mobile.trim();
  const trimmedEmail = formData.email.trim();

  // Same rule as slider: name required + 10-digit mobile + optional valid email
  const isFormValid =
    trimmedName.length > 0 &&
    isValidMobile(trimmedMobile) &&
    (trimmedEmail.length === 0 || isValidEmail(trimmedEmail));

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      const digitsOnly = value.replace(/[^0-9]/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, mobile: digitsOnly }));
      if (errorMsg) setErrorMsg("");
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorMsg) setErrorMsg("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMsg("");

    // Match slider's API payload
    const payload = new FormData();
    payload.append("name", trimmedName);
    payload.append("mobileNumber", trimmedMobile);
    payload.append("email", trimmedEmail);

    try {
      const response = await fetch(SummaryApi.popupForm.url, {
        method: "POST",
        body: payload,
      });

      if (!response.ok) {
        throw new Error("Failed to submit the form. Please try again.");
      }

      setIsSubmitted(true);
      setFormData({ fullName: "", mobile: "", email: "" });
      setTimeout(() => {
        setIsSubmitted(false);
        if (onClose) onClose();
      }, 1500);
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-neutral-100 relative w-full max-w-[420px] my-auto">
        <button
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-2xl leading-none w-8 h-8 flex items-center justify-center"
          onClick={onClose}
          aria-label="Close form"
        >
          &times;
        </button>

        <h3 className="text-xl font-serif text-center font-semibold text-neutral-800 mb-2 mt-1">
          Enquire Now
        </h3>
        <p className="text-xs text-neutral-500 text-center mb-5 leading-relaxed">
          Start your journey with Siddhi Ganapathi Developers.
          <br />
          Limited plots available—secure your investment now.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Full Name */}
          <div>
            <label className="block text-xs font-semibold text-neutral-800 mb-1.5">
              Full Name
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder="Enter your Full Name"
              required
              className="w-full px-3.5 py-2.5 text-sm rounded-lg border border-gray text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#00875a]/40 focus:bg-white transition-all"
            />
          </div>

          {/* Mobile */}
          <div>
            <label className="block text-xs font-semibold text-neutral-800 mb-1.5">
              Mobile
            </label>
            <input
              type="tel"
              name="mobile"
              value={formData.mobile}
              onChange={handleInputChange}
              placeholder="Enter your Mobile Number"
              maxLength={10}
              inputMode="numeric"
              required
              className="w-full px-3.5 py-2.5 text-sm rounded-lg bg-white border border-neutral-300 text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#00875a]/40 focus:border-[#00875a] transition-all"
            />
            {formData.mobile.length > 0 && !isValidMobile(formData.mobile) && (
              <p className="text-[11px] text-red-500 mt-1">
                Enter exactly 10 numeric digits.
              </p>
            )}
          </div>

          {/* Email (optional) */}
          <div>
            <label className="block text-xs font-semibold text-neutral-800 mb-1.5">
              Email <span className="text-neutral-500 font-normal">(optional)</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email address"
              className="w-full px-3.5 py-2.5 text-sm rounded-lg bg-white border border-neutral-300 text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#00875a]/40 focus:border-[#00875a] transition-all"
            />
            {formData.email.length > 0 && !isValidEmail(formData.email) && (
              <p className="text-[11px] text-red-500 mt-1">
                Enter a valid email address.
              </p>
            )}
          </div>

          {/* Submit */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className="w-full py-3 bg-[#008a5e] hover:bg-[#00744f] active:scale-[0.99] text-white font-bold text-sm tracking-wider uppercase rounded-xl shadow-md transition-all cursor-pointer disabled:cursor-not-allowed"
            >
              {isSubmitting ? "SUBMITTING..." : "SUBMIT"}
            </button>
          </div>

          {errorMsg && (
            <p className="text-xs text-center text-red-500 font-medium pt-1">
              {errorMsg}
            </p>
          )}

          {isSubmitted && (
            <p className="text-xs text-center text-emerald-600 font-medium pt-1">
              Thank you! Your enquiry has been received.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default Popup;